import { useCallback, useEffect, useRef, useState } from 'react';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

interface UseRequestOptions<TData> {
  enabled?: boolean;
  onSuccess?: (data: TData) => void;
  onError?: (error: string) => void;
  dependencies?: readonly unknown[];
}

interface UseRequestResult<TData> {
  isLoading: boolean;
  refetch: () => Promise<void>;
  data: TData | null;
}

function useApiRequest<TData>(
  queryFnOrArray: (() => Promise<TData>) | readonly (() => Promise<any>)[],
  options: UseRequestOptions<TData> = {},
): UseRequestResult<TData> {
  const { enabled = true, dependencies = [] } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TData | null>(null);

  const { showError } = useStoreActions(actions => actions.snackbar);

  const abortControllerRef = useRef<AbortController>();

  const queryFnRef = useRef(queryFnOrArray);
  const optionsRef = useRef(options);

  queryFnRef.current = queryFnOrArray;
  optionsRef.current = options;

  const fetchData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsLoading(true);

    try {
      let result: TData;

      if (Array.isArray(queryFnRef.current)) {
        const results = await Promise.all(queryFnRef.current.map(fn => fn()));
        result = results as TData;
        setData(result);
      } else {
        result = await (queryFnRef.current as () => Promise<TData>)();
        setData(result);
      }

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      optionsRef.current.onSuccess?.(result);
    } catch (err) {
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      const errorMessage = extractErrorMessage(err);
      showError(errorMessage);
      optionsRef.current.onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = useCallback(async () => fetchData(), [fetchData]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...dependencies]);

  useEffect(
    () => () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  return {
    isLoading,
    refetch,
    data,
  };
}

export default useApiRequest;
